import React, { useEffect, useState, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Typography from "@mui/material/Typography";


import { FONT_HEADING } from "../theme";
import { Loader } from "../components/atoms/Loader";
import { initZoho } from "../util/zohoUtil";
import { ensureDefaultsForAllStores } from "../util/ensureDefaultSetup";
import ShowError from "../components/atoms/ShowError";
import SnackAlert from "../components/atoms/SnackAlert";
import {
  getCloseSnakData,
  snackDetails,
  getOpenSnakSuccessData,
  getOpenSnakErrorData,
} from "../config/otherConstant";
import ShopifyStoresTab from "../components/PageComponent/Authentication/ShopifyStoresTab";
import ZohoAuthTab from "../components/PageComponent/Authentication/ZohoAuthTab";
import FieldMappingGroupedDemo from "../components/PageComponent/FieldMapping/FieldMappingGroupedDemo";
import RecordViewSetting from "../components/PageComponent/RecordViewSetting/RecordViewSetting";
import Webhook from "../components/PageComponent/Webhook/Webhook";
import Logs from "../components/PageComponent/Logs/Logs";
import HistoricalData from "../components/PageComponent/HistoricalData/HistoricalData";
import Dashboard from "../components/PageComponent/Dashboard/Dashboard";

// Historical Sync visibility. The feature's UI (wizard + job list) is fully
// built, but the backend worker that executes scheduled imports isn't
// implemented yet — so the tab is hidden until it is. Flip to true to bring
// it back; everything (component, seeding, routes) is kept intact.
const SHOW_HISTORICAL_SYNC = false;

const SETTINGS_TABS = [
  {
    key: "dashboard",
    label: "Dashboard",
    subtitle: "Overview of your Shopify ↔ Zoho CRM integration",
    alwaysEnabled: true,
  },
  {
    key: "stores",
    label: "Stores",
    subtitle: "Connect and manage your Shopify stores",
    alwaysEnabled: true,
  },
  {
    key: "auth",
    label: "Zoho Connection",
    subtitle: "Connect and manage your Zoho CRM connection",
    needsShopify: true,
  },
  {
    key: "fieldmap_demo",
    label: "Sync Rules",
    subtitle: "Predefined sync configurations between Shopify and Zoho CRM",
    needsBoth: true,
  },
  {
    key: "recordview",
    label: "Record View Setting",
    subtitle: "Configure related-list settings for Shopify on Zoho records",
    needsBoth: true,
  },
  {
    key: "historical",
    label: "Historical Sync",
    subtitle: "Import historical Shopify data into Zoho CRM",
    needsBoth: true,
  },
  {
    key: "webhook",
    label: "Webhooks",
    subtitle: "Monitor Shopify webhook delivery and health",
    needsBoth: true,
  },
  {
    key: "logs",
    label: "Logs",
    subtitle: "Audit log of sync activity and errors",
    needsBoth: true,
  },
];

const SIDEBAR_WIDTH = 232;
const BRAND = {
  accent: "#0f766e",       // teal logo tile
  bg: "#f7f8fa",           // app bg
  surface: "#ffffff",
  border: "#e4e4e7",
  text: "#09090b",
  muted: "#71717a",
  primary: "#0f172a",      // dark CTA / avatar
};

function SidebarNavItem({ tab, active, enabled, onClick }) {
  return (
    <Box
      component="button"
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        textAlign: "left",
        border: "none",
        background: active ? "#f4f4f5" : "transparent",
        color: active ? BRAND.text : BRAND.muted,
        fontFamily: "inherit",
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        padding: "9px 12px",
        borderRadius: "8px",
        cursor: enabled ? "pointer" : "not-allowed",
        opacity: enabled ? 1 : 0.45,
        transition: "background 0.12s, color 0.12s",
        "&:hover": enabled && !active ? { background: "#fafafa", color: BRAND.text } : {},
      }}
    >
      {tab.label}
    </Box>
  );
}

export default function SettingsRoute() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stores");
  const [snack, setSnack] = useState(snackDetails);

  // Extension data
  const [orgId, setOrgId] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [zApiKey, setZApiKey] = useState(null);
  const [dataCenterUrl, setDataCenterUrl] = useState(null);
  const [dataCenter, setDataCenter] = useState(null);
  const [orgVarMaps, setOrgVarMaps] = useState(null);
  const [shop, setShop] = useState("");
  const [shopifyStores, setShopifyStores] = useState([]);
  // Org-level identifier set on the Zoho side. The Webhooks tab is only
  // exposed when this equals "zuid" — other builds shouldn't surface
  // webhook administration to the merchant.
  const [zuid, setZuid] = useState("");

  // Bumped after default settings/webhooks are created so FieldMapping re-fetches
  const [settingsRefreshKey, setSettingsRefreshKey] = useState(0);

  // Auth states
  const [shopifyAuth, setShopifyAuth] = useState(false);
  const [zohoAuth, setZohoAuth] = useState(false);

  // True while default field mappings + webhooks are being created — on a new
  // Shopify store connect, on Zoho authentication, and via the on-load safety
  // net. While true the tab bar is locked and a blocking dialog is shown so the
  // user can't navigate away mid-setup.
  //
  // Backed by a counter (not a plain boolean) so several setup runs can overlap
  // — e.g. the Zoho-auth handler and the safety net both firing, or multiple
  // stores being provisioned — and the dialog only closes once EVERY run has
  // finished. `setSetupInProgress(true/false)` increments/decrements the count,
  // preserving the simple boolean API its callers already use.
  const [setupCount, setSetupCount] = useState(0);
  const setupInProgress = setupCount > 0;
  const setSetupInProgress = useCallback((on) => {
    setSetupCount((c) => (on ? c + 1 : Math.max(0, c - 1)));
  }, []);

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => getCloseSnakData(prev));
  };

  const isTabEnabled = useCallback(
    (tab) => {
      if (tab.alwaysEnabled) return true;
      if (tab.needsShopify) return shopifyAuth;
      if (tab.needsBoth) return shopifyAuth && zohoAuth;
      return true;
    },
    [shopifyAuth, zohoAuth]
  );

  // Auto-navigate when auth state changes. Suppressed while a new Shopify
  // store is being set up — the route happens once setupInProgress flips
  // back to false so the user lands on Zoho auth right after setup completes.
  useEffect(() => {
    if (setupInProgress) return;
    if (shopifyAuth && zohoAuth) {
      // Everything's wired up — land on Dashboard for the at-a-glance view.
      setActiveTab("dashboard");
    } else if (shopifyAuth && !zohoAuth) {
      // Shopify is connected but Zoho isn't — push to the Zoho Connection tab.
      setActiveTab("auth");
    } else {
      // No Shopify store yet (or both missing) — Stores tab handles both cases.
      setActiveTab("stores");
    }
  }, [shopifyAuth, zohoAuth, setupInProgress]);

  useEffect(() => {
    initZoho((error, extensionData) => {
      if (error || !extensionData) {
        setLoading(false);
        return;
      }

      setOrgId(extensionData.orgId);
      setApiKey(extensionData.apiKey);
      setDataCenterUrl(extensionData.dataCenterUrl);
      setDataCenter(extensionData.dataCenter);
      setZApiKey(extensionData.zApiKey);
      setOrgVarMaps(extensionData.orgVarMaps);
      setShop(extensionData.shop || "");
      setShopifyStores(extensionData.shopifyStores || []);
      setShopifyAuth(extensionData.shopifyAuth || false);
      setZohoAuth(extensionData.zohoAuth || false);
      setZuid(extensionData.zuid || "");

      setLoading(false);
    });
  }, []);

  // ── Default-setup safety net ──
  // The connect-time triggers (Shopify post-install, Zoho post-auth polling)
  // only fire in narrow windows and live inside tabs that unmount once both
  // connections are live. So whenever the widget loads with BOTH Shopify and
  // Zoho connected, ensure the default sync rules + webhooks exist for every
  // connected store. ensureDefaultsForAllStores is idempotent (skips anything
  // that already exists), so this is safe to run on every load. Guarded by a
  // ref so it only fires once per mount.
  const defaultsEnsuredRef = useRef(false);
  useEffect(() => {
    if (defaultsEnsuredRef.current) return;
    if (!orgId || !apiKey || !dataCenterUrl) return;
    if (!shopifyAuth || !zohoAuth) return;
    const storeDomains = (shopifyStores || [])
      .map((s) => s?.domain || s?.storeDomain || s?.shop)
      .filter(Boolean);
    if (storeDomains.length === 0) return;

    defaultsEnsuredRef.current = true;
    (async () => {
      // Block the UI until every store's default settings + webhooks are done.
      setSetupInProgress(true);
      try {
        const { created, warnings } = await ensureDefaultsForAllStores({
          orgId,
          apiKey,
          dataCenterUrl,
          dataCenter,
          zapikey: zApiKey,
          storeDomains,
          services: ["Zoho CRM"],
        });
        if (created.length > 0) {
          // New defaults were created — bump the key so Sync Rules re-fetches.
          setSettingsRefreshKey((k) => k + 1);
          setSnack(
            getOpenSnakSuccessData(
              `Created ${created.length} default sync rule${
                created.length === 1 ? "" : "s"
              }.`
            )
          );
        } else if (warnings.length > 0) {
          // Ran but couldn't create anything — surface the real reason instead
          // of failing silently.
          setSnack(
            getOpenSnakErrorData(
              `Default setup couldn't create sync rules: ${warnings
                .slice(0, 2)
                .join(" | ")}`,
              9000
            )
          );
        }
      } catch (e) {
        console.error("[SettingsRoute] default setup safety-net failed:", e);
        setSnack(
          getOpenSnakErrorData(
            "Default setup failed to run. Please refresh and try again.",
            9000
          )
        );
      } finally {
        setSetupInProgress(false);
      }
    })();
  }, [
    orgId,
    apiKey,
    dataCenterUrl,
    dataCenter,
    zApiKey,
    shopifyAuth,
    zohoAuth,
    shopifyStores,
    // Stable useCallback — included to satisfy exhaustive-deps.
    setSetupInProgress,
  ]);

  if (loading) return <Loader />;
  if (!orgId || !apiKey || !dataCenterUrl) return <ShowError />;

  const commonProps = {
    orgId,
    apiKey,
    zApiKey,
    dataCenterUrl,
    dataCenter,
    orgVarMaps,
    setOrgVarMaps,
    shop,
    setShop,
    shopifyStores,
    setShopifyStores,
    setSnack,
  };

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden", bgcolor: BRAND.bg }}>
      {/* ── Sidebar (peer in flex row — stretches via cross-axis) ── */}
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: BRAND.surface,
          borderRight: `1px solid ${BRAND.border}`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Brand header — text-only wordmark (the icon image reads poorly at
            sidebar size, so it isn't rendered here). */}
        <Box
          sx={{
            px: 2.5,
            py: 2.25,
            borderBottom: `1px solid ${BRAND.border}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontWeight: 700,
              fontSize: 19,
              lineHeight: 1.15,
              color: BRAND.text,
              letterSpacing: "-0.01em",
            }}
          >
            Easy Shopify
          </Typography>
          <Typography sx={{ fontSize: 14, color: BRAND.muted, mt: "2px" }}>
            for Zoho CRM
          </Typography>
        </Box>

        {/* Nav items */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 1.25,
            py: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
          }}
        >
          {SETTINGS_TABS.filter(
            (tab) => tab.key !== "webhook" || zuid === "zuid"
          )
            .filter(
              (tab) => tab.key !== "historical" || SHOW_HISTORICAL_SYNC
            )
            .map((tab) => {
            const enabled = isTabEnabled(tab) && !setupInProgress;
            return (
              <SidebarNavItem
                key={tab.key}
                tab={tab}
                active={activeTab === tab.key}
                enabled={enabled}
                onClick={() => setActiveTab(tab.key)}
              />
            );
          })}
        </Box>

        <Typography
          sx={{
            mt: "auto",
            flexShrink: 0,
            fontSize: 11,
            color: "#a1a1aa",
            textAlign: "center",
            py: 1.25,
            px: 1,
            borderTop: `1px solid ${BRAND.border}`,
            bgcolor: BRAND.surface,
          }}
        >
          
        </Typography>
        {/* © Easy Pluginz Pty Ltd */}
      </Box>

      {/* ── Main column (peer in flex row, scrolls internally) ── */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Tab Content */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
          {activeTab === "dashboard" && <Dashboard {...commonProps} />}
          {activeTab === "stores" && (
            <ShopifyStoresTab
              {...commonProps}
              shopifyAuth={shopifyAuth}
              setShopifyAuth={setShopifyAuth}
              zohoAuth={zohoAuth}
              setSetupInProgress={setSetupInProgress}
              onSettingsCreated={() => setSettingsRefreshKey((k) => k + 1)}
            />
          )}
          {activeTab === "auth" && (
            <ZohoAuthTab
              {...commonProps}
              zohoAuth={zohoAuth}
              setZohoAuth={setZohoAuth}
              setSetupInProgress={setSetupInProgress}
              onSettingsCreated={() => setSettingsRefreshKey((k) => k + 1)}
            />
          )}
          {activeTab === "fieldmap_demo" && (
            <FieldMappingGroupedDemo key={settingsRefreshKey} {...commonProps} />
          )}
          {activeTab === "recordview" && <RecordViewSetting {...commonProps} />}
          {activeTab === "webhook" && <Webhook {...commonProps} />}
          {activeTab === "logs" && <Logs {...commonProps} />}
          {SHOW_HISTORICAL_SYNC && activeTab === "historical" && (
            <HistoricalData {...commonProps} />
          )}
        </Box>
      </Box>

      <SnackAlert
        duration={snack?.duration}
        snackOpen={snack?.open}
        handleCloseSnack={handleCloseSnack}
        severity={snack?.severity}
        message={snack?.message}
      />

      {/* Blocking modal shown while default field mappings + webhooks are being
          created — on store connect, on Zoho authentication, and via the
          on-load safety net. Cannot be dismissed; tabs are locked while up, and
          it stays open until EVERY in-flight setup run (all stores) finishes. */}
      <Dialog
        open={setupInProgress}
        disableEscapeKeyDown
        hideBackdrop={false}
        PaperProps={{ sx: { borderRadius: "10px", p: 1, maxWidth: 440 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={20} />
          <Typography component="span" sx={{ fontSize: 17, fontWeight: 600 }}>
            Setting up your integration
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, color: "#3f3f46", mb: 1.5 }}>
            Creating the default sync rules and webhooks for your connected
            store(s). This usually takes a few seconds.
          </DialogContentText>
          <Box
            sx={{
              p: "10px 12px",
              bgcolor: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: "6px",
              fontSize: 13,
              color: "#92400e",
            }}
          >
            ⚠️ Please don't switch tabs or close this page until setup finishes.
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
